"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.saveDealMilestoneRequestSchema = exports.saveDealLineItemRequestSchema = exports.updateDealEventRequestSchema = exports.createDealEventRequestSchema = exports.markDealLostRequestSchema = exports.markDealWonRequestSchema = exports.setNextStepRequestSchema = exports.setDealStatusRequestSchema = exports.updateDealRequestSchema = exports.createDealRequestSchema = exports.dealDemandSchema = exports.dealForecastSchema = exports.dealBoardSchema = exports.dealListTotalsSchema = exports.dealPickersSchema = exports.dealDetailSchema = exports.dealPlanTotalsSchema = exports.dealCostSchema = exports.dealAllocationSchema = exports.dealPlaceholderSchema = exports.dealLineItemSchema = exports.dealMilestoneSchema = exports.dealEventSchema = exports.dealSchema = exports.saveContactRequestSchema = exports.contactSchema = exports.updateDealSettingsRequestSchema = exports.savePlaceholderProfileRequestSchema = exports.saveDealReasonRequestSchema = exports.saveDealProbabilityRequestSchema = exports.reorderRequestSchema = exports.saveDealStatusRequestSchema = exports.dealConfigSchema = exports.dealSettingsSchema = exports.placeholderProfileSchema = exports.dealReasonSchema = exports.dealProbabilitySchema = exports.dealStatusSchema = exports.DEAL_LIST_VIEWS = exports.DEAL_REASON_KINDS = exports.DEAL_EVENT_KINDS = exports.DEAL_RATE_UNITS = exports.DEAL_LINE_ITEM_STATUSES = exports.DEAL_LINE_ITEM_TYPES = exports.DEAL_REVENUE_MODES = exports.DEAL_VALUE_MODES = exports.SENIORITIES = exports.DEAL_SOURCES = exports.FORECAST_CATEGORIES = exports.DEAL_STATUS_CATEGORIES = void 0;
exports.generateDealResultSchema = exports.generateDealRequestSchema = exports.DEAL_GENERATE_ROUTES = exports.saveDealCostRequestSchema = exports.setDealAllocationsRequestSchema = exports.updateDealPlaceholderRequestSchema = exports.createDealPlaceholderRequestSchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · PHASE 6 · DEAL MANAGEMENT — every shape on the wire.        │
 * │ `docs/mockup/PHASE-6.md`, decisions.md DM-1…DM-7, `0227`–`0229`.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Money crosses as a plain decimal string (`"21000000.00"`), hours as a decimal string, calendar
 * dates as `YYYY-MM-DD`, instants as ISO strings — the same conventions as every other contract.
 */
const wireDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
const money = zod_1.z.string();
const hours = zod_1.z.string();
const color = zod_1.z.string().regex(/^#[0-9A-Fa-f]{6}$/u);
const moneyInput = zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).transform((value, ctx) => {
    const n = typeof value === 'number' ? value : Number(value.replace(/\s/gu, '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) {
        ctx.addIssue({ code: 'custom', message: 'not an amount' });
        return zod_1.z.NEVER;
    }
    return Math.round(n * 100) / 100;
});
/* ── vocabularies ──────────────────────────────────────────────────────────────────────────── */
exports.DEAL_STATUS_CATEGORIES = ['open', 'won', 'lost'];
/**
 * ⚠️ Fixed, not a table — the mockup draws them read-only (`0227`'s header). Derived from the
 * probability unless a deal overrides it (`forecastCategoryOf`).
 */
exports.FORECAST_CATEGORIES = ['commit', 'best_case', 'pipeline', 'omitted'];
exports.DEAL_SOURCES = [
    'inbound',
    'referral',
    'outbound',
    'existing',
    'tender',
    'partner',
];
exports.SENIORITIES = ['junior', 'medior', 'senior'];
exports.DEAL_VALUE_MODES = ['indicative', 'plan'];
exports.DEAL_REVENUE_MODES = ['one_off', 'recurring', 'milestones', 'manual'];
exports.DEAL_LINE_ITEM_TYPES = ['fixed', 'tm', 'license', 'option'];
exports.DEAL_LINE_ITEM_STATUSES = ['draft', 'open', 'closed'];
exports.DEAL_RATE_UNITS = ['hour', 'day', 'month'];
exports.DEAL_EVENT_KINDS = ['note', 'task', 'meeting', 'call', 'email', 'system'];
exports.DEAL_REASON_KINDS = ['lost', 'won'];
/** The list's saved views (the mockup's `dealListViews`). */
exports.DEAL_LIST_VIEWS = [
    'open',
    'all',
    'mine',
    'rot',
    'nonext',
    'quarter',
    'slip',
    'won',
    'lost',
    'conv',
];
/* ── settings ──────────────────────────────────────────────────────────────────────────────── */
exports.dealStatusSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    color,
    category: zod_1.z.enum(exports.DEAL_STATUS_CATEGORIES),
    probability: zod_1.z.number().int(),
    rotDays: zod_1.z.number().int(),
    criteria: zod_1.z.string(),
    sortOrder: zod_1.z.number().int(),
    dealCount: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
exports.dealProbabilitySchema = zod_1.z.object({
    id: zod_1.z.string(),
    pct: zod_1.z.number().int(),
    name: zod_1.z.string(),
    description: zod_1.z.string(),
    color,
    version: zod_1.z.number().int(),
});
exports.dealReasonSchema = zod_1.z.object({
    id: zod_1.z.string(),
    kind: zod_1.z.enum(exports.DEAL_REASON_KINDS),
    name: zod_1.z.string(),
    groupName: zod_1.z.string(),
    active: zod_1.z.boolean(),
    needsNote: zod_1.z.boolean(),
    dealCount: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
exports.placeholderProfileSchema = zod_1.z.object({
    id: zod_1.z.string(),
    projectRoleId: zod_1.z.string(),
    projectRoleName: zod_1.z.string(),
    projectRoleColor: zod_1.z.string(),
    seniority: zod_1.z.enum(exports.SENIORITIES),
    costRate: money,
    targetRate: money,
    /** How many deal placeholders were made from it. */
    usage: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
exports.dealSettingsSchema = zod_1.z.object({
    nextStepRequired: zod_1.z.boolean(),
    tentativeBooking: zod_1.z.boolean(),
    quarterlyTarget: money,
    /** The workspace's default currency — what the target and the profiles' rates are in. */
    currencyCode: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
/** Everything the Deal menedzsment settings and every deal screen's pickers read, in one request. */
exports.dealConfigSchema = zod_1.z.object({
    statuses: zod_1.z.array(exports.dealStatusSchema),
    probabilities: zod_1.z.array(exports.dealProbabilitySchema),
    reasons: zod_1.z.array(exports.dealReasonSchema),
    profiles: zod_1.z.array(exports.placeholderProfileSchema),
    settings: exports.dealSettingsSchema,
    /** Open deals per forecast category: the settings tab's count and sum. */
    forecast: zod_1.z.array(zod_1.z.object({ category: zod_1.z.enum(exports.FORECAST_CATEGORIES), count: zod_1.z.number().int(), value: money })),
});
exports.saveDealStatusRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(60),
    color,
    /** Only on create; a won or lost status exists exactly once and is never made here. */
    probability: zod_1.z.number().int().min(0).max(100),
    rotDays: zod_1.z.number().int().min(0).max(365),
    criteria: zod_1.z.string().max(400),
    version: zod_1.z.number().int().positive().optional(),
});
exports.reorderRequestSchema = zod_1.z.strictObject({ ids: zod_1.z.array(zod_1.z.uuid()).min(1).max(200) });
exports.saveDealProbabilityRequestSchema = zod_1.z.strictObject({
    pct: zod_1.z.number().int().min(0).max(100),
    name: zod_1.z.string().trim().min(1).max(60),
    description: zod_1.z.string().max(200),
    color,
    version: zod_1.z.number().int().positive().optional(),
});
exports.saveDealReasonRequestSchema = zod_1.z.strictObject({
    kind: zod_1.z.enum(exports.DEAL_REASON_KINDS),
    name: zod_1.z.string().trim().min(1).max(80),
    groupName: zod_1.z.string().max(60),
    active: zod_1.z.boolean(),
    needsNote: zod_1.z.boolean(),
    version: zod_1.z.number().int().positive().optional(),
});
exports.savePlaceholderProfileRequestSchema = zod_1.z.strictObject({
    projectRoleId: zod_1.z.uuid(),
    seniority: zod_1.z.enum(exports.SENIORITIES),
    costRate: moneyInput,
    targetRate: moneyInput,
    version: zod_1.z.number().int().positive().optional(),
});
exports.updateDealSettingsRequestSchema = zod_1.z.strictObject({
    nextStepRequired: zod_1.z.boolean(),
    tentativeBooking: zod_1.z.boolean(),
    quarterlyTarget: moneyInput,
    version: zod_1.z.number().int().positive(),
});
/* ── contacts (DM-2) ───────────────────────────────────────────────────────────────────────── */
exports.contactSchema = zod_1.z.object({
    id: zod_1.z.string(),
    identifier: (0, identifier_1.identifierSchema)('contact'),
    clientId: zod_1.z.string(),
    name: zod_1.z.string(),
    email: zod_1.z.string().nullable(),
    phone: zod_1.z.string(),
    title: zod_1.z.string(),
    isPrimary: zod_1.z.boolean(),
    version: zod_1.z.number().int(),
});
exports.saveContactRequestSchema = zod_1.z.strictObject({
    clientId: zod_1.z.uuid(),
    name: zod_1.z.string().trim().min(1).max(120),
    email: zod_1.z.email().max(254).nullable(),
    phone: zod_1.z.string().max(40),
    title: zod_1.z.string().max(80),
    isPrimary: zod_1.z.boolean(),
    version: zod_1.z.number().int().positive().optional(),
});
/* ── the deal ──────────────────────────────────────────────────────────────────────────────── */
const personRef = zod_1.z.object({
    memberId: zod_1.z.string(),
    name: zod_1.z.string(),
    avatarUrl: zod_1.z.string().nullable(),
});
exports.dealSchema = zod_1.z.object({
    id: zod_1.z.string(),
    identifier: (0, identifier_1.identifierSchema)('deal'),
    name: zod_1.z.string(),
    clientId: zod_1.z.string(),
    clientName: zod_1.z.string(),
    clientColor: zod_1.z.string().nullable(),
    contactId: zod_1.z.string().nullable(),
    contactName: zod_1.z.string().nullable(),
    ownerMemberId: zod_1.z.string(),
    ownerName: zod_1.z.string(),
    ownerAvatarUrl: zod_1.z.string().nullable(),
    statusId: zod_1.z.string(),
    statusName: zod_1.z.string(),
    statusColor: zod_1.z.string(),
    statusCategory: zod_1.z.enum(exports.DEAL_STATUS_CATEGORIES),
    probability: zod_1.z.number().int(),
    forecastCategory: zod_1.z.enum(exports.FORECAST_CATEGORIES),
    forecastOverridden: zod_1.z.boolean(),
    source: zod_1.z.enum(exports.DEAL_SOURCES),
    valueMode: zod_1.z.enum(exports.DEAL_VALUE_MODES),
    /** The deal's value: the plan's sum or the indicative figure, by `valueMode`. */
    value: money,
    weightedValue: money,
    currencyCode: zod_1.z.string(),
    plannedHours: hours,
    closeDate: wireDate.nullable(),
    nextStep: zod_1.z
        .object({
        text: zod_1.z.string(),
        due: wireDate.nullable(),
        memberId: zod_1.z.string().nullable(),
        memberName: zod_1.z.string().nullable(),
    })
        .nullable(),
    nextStepLate: zod_1.z.boolean(),
    daysInStage: zod_1.z.number().int(),
    rotting: zod_1.z.boolean(),
    slipped: zod_1.z.boolean(),
    lastActivityAt: zod_1.z.string().nullable(),
    openTodos: zod_1.z.number().int(),
    favourite: zod_1.z.boolean(),
    converted: zod_1.z.boolean(),
    tags: zod_1.z.array(zod_1.z.string()),
    version: zod_1.z.number().int(),
});
exports.dealEventSchema = zod_1.z.object({
    id: zod_1.z.string(),
    kind: zod_1.z.enum(exports.DEAL_EVENT_KINDS),
    title: zod_1.z.string(),
    body: zod_1.z.string(),
    dueAt: zod_1.z.string().nullable(),
    done: zod_1.z.boolean(),
    assigneeMemberId: zod_1.z.string().nullable(),
    assigneeName: zod_1.z.string().nullable(),
    participants: zod_1.z.string(),
    authorName: zod_1.z.string().nullable(),
    occurredAt: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
exports.dealMilestoneSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    dueOn: wireDate.nullable(),
    version: zod_1.z.number().int(),
});
exports.dealLineItemSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    type: zod_1.z.enum(exports.DEAL_LINE_ITEM_TYPES),
    status: zod_1.z.enum(exports.DEAL_LINE_ITEM_STATUSES),
    milestoneId: zod_1.z.string().nullable(),
    milestoneName: zod_1.z.string().nullable(),
    startsOn: wireDate.nullable(),
    endsOn: wireDate.nullable(),
    estimateHours: hours,
    allocatedHours: hours,
    costBudget: money,
    amount: money,
    currencyCode: zod_1.z.string(),
    rate: money.nullable(),
    rateUnit: zod_1.z.enum(exports.DEAL_RATE_UNITS),
    quantity: zod_1.z.string().nullable(),
    sortOrder: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
exports.dealPlaceholderSchema = zod_1.z.object({
    id: zod_1.z.string(),
    profileId: zod_1.z.string().nullable(),
    projectRoleId: zod_1.z.string().nullable(),
    projectRoleName: zod_1.z.string().nullable(),
    projectRoleColor: zod_1.z.string().nullable(),
    seniority: zod_1.z.enum(exports.SENIORITIES).nullable(),
    memberId: zod_1.z.string().nullable(),
    memberName: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    headcount: zod_1.z.number().int(),
    costRate: money,
    allocatedHours: hours,
    version: zod_1.z.number().int(),
});
exports.dealAllocationSchema = zod_1.z.object({
    lineItemId: zod_1.z.string(),
    placeholderId: zod_1.z.string(),
    weekStart: wireDate,
    hours,
});
exports.dealCostSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    amount: money,
    currencyCode: zod_1.z.string(),
    incurredOn: wireDate.nullable(),
    version: zod_1.z.number().int(),
});
exports.dealPlanTotalsSchema = zod_1.z.object({
    estimateHours: hours,
    allocatedHours: hours,
    laborCost: money,
    externalCost: money,
    revenue: money,
    margin: money,
    /** 0..1, or null with no revenue. */
    marginPct: zod_1.z.number().nullable(),
    startsOn: wireDate.nullable(),
    endsOn: wireDate.nullable(),
});
exports.dealDetailSchema = exports.dealSchema.extend({
    campaign: zod_1.z.string(),
    competitor: zod_1.z.string(),
    revenueMode: zod_1.z.enum(exports.DEAL_REVENUE_MODES),
    indicativeValue: money,
    createdAt: zod_1.z.string(),
    createdByName: zod_1.z.string().nullable(),
    contact: zod_1.z
        .object({
        id: zod_1.z.string(),
        name: zod_1.z.string(),
        title: zod_1.z.string(),
        email: zod_1.z.string().nullable(),
        phone: zod_1.z.string(),
    })
        .nullable(),
    otherContacts: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), title: zod_1.z.string() })),
    followers: zod_1.z.array(personRef),
    stages: zod_1.z.array(zod_1.z.object({ statusId: zod_1.z.string(), enteredAt: zod_1.z.string(), leftAt: zod_1.z.string().nullable() })),
    closeHistory: zod_1.z.array(zod_1.z.object({
        fromDate: wireDate.nullable(),
        toDate: wireDate.nullable(),
        reason: zod_1.z.string(),
        changedByName: zod_1.z.string().nullable(),
        changedAt: zod_1.z.string(),
    })),
    closedOn: wireDate.nullable(),
    wonValue: money.nullable(),
    reasonId: zod_1.z.string().nullable(),
    reasonName: zod_1.z.string().nullable(),
    closeNote: zod_1.z.string(),
    events: zod_1.z.array(exports.dealEventSchema),
    milestones: zod_1.z.array(exports.dealMilestoneSchema),
    lineItems: zod_1.z.array(exports.dealLineItemSchema),
    placeholders: zod_1.z.array(exports.dealPlaceholderSchema),
    allocations: zod_1.z.array(exports.dealAllocationSchema),
    costs: zod_1.z.array(exports.dealCostSchema),
    conversions: zod_1.z.array(zod_1.z.object({
        projectId: zod_1.z.string().nullable(),
        projectName: zod_1.z.string().nullable(),
        contractId: zod_1.z.string().nullable(),
        contractIdentifier: zod_1.z.string().nullable(),
        contractName: zod_1.z.string().nullable(),
        createdAt: zod_1.z.string(),
    })),
    plan: exports.dealPlanTotalsSchema,
    /** What the reader may do here — the screen's gating, never the security. */
    canManage: zod_1.z.boolean(),
});
/**
 * The deal screens' own pickers — the companies a deal can be for and the people who can own,
 * follow or do its next step. ⚠️ **Gated on `deal.view`, not on `client.view` / `people.view`**: a
 * role that may manage deals but not browse the client list or the people directory must still be
 * able to name a company and an owner. Names and colours only, nothing else about either.
 */
exports.dealPickersSchema = zod_1.z.object({
    clients: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), color: zod_1.z.string().nullable() })),
    members: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), avatarSeed: zod_1.z.string().nullable() })),
    /** The workspace's active currencies — what a deal's value may be in. */
    currencies: zod_1.z.array(zod_1.z.object({ code: zod_1.z.string(), name: zod_1.z.string(), symbol: zod_1.z.string() })),
});
/** The list's footer: the whole filtered set's sums, not the page's. */
exports.dealListTotalsSchema = zod_1.z.object({
    count: zod_1.z.number().int(),
    value: money,
    weighted: money,
    committed: money,
    currencyCode: zod_1.z.string(),
});
/* ── the pipeline board ────────────────────────────────────────────────────────────────────── */
exports.dealBoardSchema = zod_1.z.object({
    columns: zod_1.z.array(zod_1.z.object({
        statusId: zod_1.z.string(),
        name: zod_1.z.string(),
        color: zod_1.z.string(),
        criteria: zod_1.z.string(),
        value: money,
        weighted: money,
        deals: zod_1.z.array(exports.dealSchema),
    })),
    closed: zod_1.z.array(exports.dealSchema),
    currencyCode: zod_1.z.string(),
});
/* ── forecast and demand (D7) ──────────────────────────────────────────────────────────────── */
exports.dealForecastSchema = zod_1.z.object({
    currencyCode: zod_1.z.string(),
    target: money,
    /** e.g. `2026-Q4`. */
    quarter: zod_1.z.string(),
    pipeline: money,
    committedWeighted: money,
    /** Pipeline ÷ target, or null with no target. */
    coverage: zod_1.z.number().nullable(),
    /** First days of the months shown. */
    months: zod_1.z.array(wireDate),
    rows: zod_1.z.array(zod_1.z.object({
        category: zod_1.z.enum(exports.FORECAST_CATEGORIES),
        count: zod_1.z.number().int(),
        weighted: zod_1.z.array(money),
        raw: zod_1.z.array(money),
        totalWeighted: money,
        totalRaw: money,
    })),
    /** Revenue already under contract, per month. */
    contracted: zod_1.z.array(money),
    /** Contracted + the weighted forecast, per month. */
    expected: zod_1.z.array(money),
});
exports.dealDemandSchema = zod_1.z.object({
    /** Mondays. */
    weeks: zod_1.z.array(wireDate),
    roles: zod_1.z.array(zod_1.z.object({
        projectRoleId: zod_1.z.string(),
        name: zod_1.z.string(),
        color: zod_1.z.string(),
        demand: zod_1.z.array(hours),
        capacity: zod_1.z.array(hours),
    })),
});
/* ── requests ──────────────────────────────────────────────────────────────────────────────── */
exports.createDealRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(200),
    clientId: zod_1.z.uuid(),
    contactId: zod_1.z.uuid().nullable().optional(),
    ownerMemberId: zod_1.z.uuid(),
    statusId: zod_1.z.uuid(),
    indicativeValue: moneyInput.optional(),
    closeDate: wireDate.nullable().optional(),
    source: zod_1.z.enum(exports.DEAL_SOURCES).optional(),
});
exports.updateDealRequestSchema = zod_1.z.strictObject({
    version: zod_1.z.number().int().positive(),
    name: zod_1.z.string().trim().min(1).max(200).optional(),
    clientId: zod_1.z.uuid().optional(),
    contactId: zod_1.z.uuid().nullable().optional(),
    ownerMemberId: zod_1.z.uuid().optional(),
    probability: zod_1.z.number().int().min(0).max(100).optional(),
    forecastOverride: zod_1.z.enum(exports.FORECAST_CATEGORIES).nullable().optional(),
    source: zod_1.z.enum(exports.DEAL_SOURCES).optional(),
    campaign: zod_1.z.string().max(120).optional(),
    competitor: zod_1.z.string().max(120).optional(),
    valueMode: zod_1.z.enum(exports.DEAL_VALUE_MODES).optional(),
    indicativeValue: moneyInput.optional(),
    currencyCode: zod_1.z.string().min(3).max(3).optional(),
    revenueMode: zod_1.z.enum(exports.DEAL_REVENUE_MODES).optional(),
    closeDate: wireDate.nullable().optional(),
    /** Why the close date moved — recorded in its history. */
    closeDateReason: zod_1.z.string().max(300).optional(),
    tags: zod_1.z.array(zod_1.z.string().trim().min(1).max(40)).max(20).optional(),
    followerIds: zod_1.z.array(zod_1.z.uuid()).max(50).optional(),
    otherContactIds: zod_1.z.array(zod_1.z.uuid()).max(50).optional(),
});
exports.setDealStatusRequestSchema = zod_1.z.strictObject({ statusId: zod_1.z.uuid() });
exports.setNextStepRequestSchema = zod_1.z.strictObject({
    text: zod_1.z.string().trim().max(300),
    due: wireDate.nullable(),
    memberId: zod_1.z.uuid().nullable(),
});
exports.markDealWonRequestSchema = zod_1.z.strictObject({
    value: moneyInput,
    closedOn: wireDate,
    reasonId: zod_1.z.uuid().nullable(),
    note: zod_1.z.string().max(2000),
});
exports.markDealLostRequestSchema = zod_1.z.strictObject({
    reasonId: zod_1.z.uuid(),
    competitor: zod_1.z.string().max(120),
    closedOn: wireDate,
    note: zod_1.z.string().max(2000),
});
exports.createDealEventRequestSchema = zod_1.z.strictObject({
    kind: zod_1.z.enum(['note', 'task', 'meeting', 'call', 'email']),
    title: zod_1.z.string().max(300),
    body: zod_1.z.string().max(8000),
    dueAt: zod_1.z.string().nullable().optional(),
    assigneeMemberId: zod_1.z.uuid().nullable().optional(),
    participants: zod_1.z.string().max(300).optional(),
});
exports.updateDealEventRequestSchema = zod_1.z.strictObject({
    version: zod_1.z.number().int().positive(),
    done: zod_1.z.boolean().optional(),
    title: zod_1.z.string().max(300).optional(),
    body: zod_1.z.string().max(8000).optional(),
    dueAt: zod_1.z.string().nullable().optional(),
});
exports.saveDealLineItemRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(200),
    type: zod_1.z.enum(exports.DEAL_LINE_ITEM_TYPES),
    status: zod_1.z.enum(exports.DEAL_LINE_ITEM_STATUSES),
    milestoneId: zod_1.z.uuid().nullable(),
    startsOn: wireDate.nullable(),
    endsOn: wireDate.nullable(),
    estimateHours: zod_1.z.number().min(0).max(1_000_000),
    costBudget: moneyInput,
    amount: moneyInput,
    currencyCode: zod_1.z.string().min(3).max(3),
    rate: moneyInput.nullable(),
    rateUnit: zod_1.z.enum(exports.DEAL_RATE_UNITS),
    quantity: zod_1.z.number().min(0).max(100_000).nullable(),
    version: zod_1.z.number().int().positive().optional(),
});
exports.saveDealMilestoneRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(160),
    dueOn: wireDate.nullable(),
    version: zod_1.z.number().int().positive().optional(),
});
exports.createDealPlaceholderRequestSchema = zod_1.z
    .strictObject({
    profileId: zod_1.z.uuid().optional(),
    memberId: zod_1.z.uuid().optional(),
    name: zod_1.z.string().trim().min(1).max(120).optional(),
    headcount: zod_1.z.number().int().min(1).max(50).optional(),
})
    .refine((body) => body.profileId !== undefined || body.memberId !== undefined, {
    message: 'a placeholder needs a profile or a person',
});
exports.updateDealPlaceholderRequestSchema = zod_1.z.strictObject({
    version: zod_1.z.number().int().positive(),
    name: zod_1.z.string().trim().min(1).max(120).optional(),
    headcount: zod_1.z.number().int().min(1).max(50).optional(),
    /** DM-5 · put a real person on it (a tentative booking), or take them off. */
    memberId: zod_1.z.uuid().nullable().optional(),
});
exports.setDealAllocationsRequestSchema = zod_1.z.strictObject({
    cells: zod_1.z
        .array(zod_1.z.strictObject({
        lineItemId: zod_1.z.uuid(),
        placeholderId: zod_1.z.uuid(),
        weekStart: wireDate,
        /** 0 clears the cell. */
        hours: zod_1.z.number().min(0).max(400),
    }))
        .min(1)
        .max(500),
});
exports.saveDealCostRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(160),
    amount: moneyInput,
    currencyCode: zod_1.z.string().min(3).max(3),
    incurredOn: wireDate.nullable(),
    version: zod_1.z.number().int().positive().optional(),
});
/* ── generation (D8) ───────────────────────────────────────────────────────────────────────── */
exports.DEAL_GENERATE_ROUTES = ['both', 'contract', 'project'];
exports.generateDealRequestSchema = zod_1.z.strictObject({
    route: zod_1.z.enum(exports.DEAL_GENERATE_ROUTES),
    project: zod_1.z
        .strictObject({
        name: zod_1.z.string().trim().min(1).max(200),
        code: zod_1.z.string().trim().max(60).nullable(),
        pmMemberId: zod_1.z.uuid(),
    })
        .nullable(),
    /** `contract` route: hang the contract off an existing project, or off the client alone. */
    existingProjectId: zod_1.z.uuid().nullable(),
    contract: zod_1.z
        .strictObject({
        type: zod_1.z.enum(['milestone', 'service']),
        name: zod_1.z.string().trim().min(1).max(200),
    })
        .nullable(),
    lineItemIds: zod_1.z.array(zod_1.z.uuid()).max(200),
    milestonesAsLineItems: zod_1.z.boolean(),
    milestonesAsTasks: zod_1.z.boolean(),
    /** Placeholder → the person who takes its hours. */
    matches: zod_1.z.record(zod_1.z.string(), zod_1.z.uuid()),
});
exports.generateDealResultSchema = zod_1.z.object({
    projectId: zod_1.z.string().nullable(),
    contractId: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    lineItems: zod_1.z.number().int(),
    tasks: zod_1.z.number().int(),
    allocations: zod_1.z.number().int(),
});
//# sourceMappingURL=contract.js.map